// Напускането на работен лист — общо за доставка, изписване, прехвърляне и
// инвентаризация. Без промени — тихо. НОВ документ с чернова: тя се записва
// веднага и листът излиза тихо (нищо не се губи — продължава се от началния
// екран). Редакция: „Излизане от … без запис?" с редовете в листа
// (components/confirm/useLeaveConfirm). Пазачът се регистрира в layout-а
// (Inventory.tsx), за да питат и менюто, и стрелката „назад".
import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import { useLeaveConfirm, type UnsavedSummary } from "../confirm/useLeaveConfirm";

type Params = {
  mode: "new" | "edit";
  dirtyRef: MutableRefObject<boolean>;
  // нов документ: записва черновата веднага (без debounce-а)
  flushDraft?: () => Promise<void>;
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
  // въпросът при редакция: кой документ и кои редове; четен в момента на въпроса
  describeUnsaved: () => UnsavedSummary;
};

export const useWorksheetLeave = ({
  mode,
  dirtyRef,
  flushDraft,
  onRegisterLeaveGuard,
  describeUnsaved,
}: Params) => {
  const confirmLeave = useLeaveConfirm();
  const askingRef = useRef(false);
  // пазачът не сменя идентичността си при всяко натискане
  const describeRef = useRef(describeUnsaved);
  describeRef.current = describeUnsaved;
  const flushRef = useRef(flushDraft);
  flushRef.current = flushDraft;

  const requestLeave = useCallback(
    (proceed: () => void) => {
      if (!dirtyRef.current) {
        proceed();
        return;
      }
      const flush = flushRef.current;
      if (mode === "new" && flush) {
        void flush();
        proceed();
        return;
      }
      if (askingRef.current) return;
      askingRef.current = true;
      void confirmLeave(describeRef.current()).then((confirmed) => {
        askingRef.current = false;
        if (confirmed) proceed();
      });
    },
    [confirmLeave, mode, dirtyRef],
  );

  useEffect(() => {
    onRegisterLeaveGuard?.(requestLeave);
    return () => onRegisterLeaveGuard?.(null);
  }, [onRegisterLeaveGuard, requestLeave]);

  return requestLeave;
};
